import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import SponsorsPage from "@/pages/SponsorsPage";
import AboutPage from "@/pages/AboutPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import PlaybookIndex from "@/pages/PlaybookIndex";
import PlaybookLesson from "@/pages/PlaybookLesson";
import RoundRobinCalculator from "@/pages/RoundRobinCalculator";
import PlayerPropsPage from "@/pages/PlayerPropsPage";
import AnalyzerPage from "@/pages/AnalyzerPage";

// Scroll behavior: on route change, scroll to top (unless a hash target exists).
// Also emits a Google Analytics `page_view` event per completed route change.
// GA is configured with { send_page_view: false } in index.html so the first
// load is not double-counted — this hook is the single source of page views.
const ScrollManager = () => {
  const { pathname, hash, search } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    // Defer one tick so React finishes updating <title> via useSEO before we read it.
    const id = window.setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: pathname + search + hash,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 0);
    return () => window.clearTimeout(id);
  }, [pathname, search, hash]);

  return null;
};

function App() {
  return (
    <div className="App pa-app-bg" id="top">
      <BrowserRouter>
        <ScrollManager />
        <a
          href="#parlay-calculator"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:pa-btn focus:pa-btn-primary"
        >
          Skip to main content
        </a>
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/playbook" element={<PlaybookIndex />} />
          <Route path="/playbook/:slug" element={<PlaybookLesson />} />
          <Route path="/round-robin-calculator" element={<RoundRobinCalculator />} />
          <Route path="/player-props" element={<PlayerPropsPage />} />
          <Route path="/analyzer" element={<AnalyzerPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{ style: { fontFamily: "IBM Plex Sans, sans-serif" } }}
      />
    </div>
  );
}

export default App;
